import { useEffect, useRef, useState, type CSSProperties } from "react";

interface ScrollFadeIndicatorProps {
  /** Thumb color. Bind to a theme slot (e.g. `bodyCopy`); never hardcode. */
  color: string;
  /** How long the thumb stays visible after the last scroll event. */
  hideAfterMs?: number;
}

const MIN_THUMB_PX = 24;

/**
 * Overlay scroll thumb for a container whose native scrollbar is hidden.
 *
 * Render it as the first child inside the scrollable region. It finds the
 * nearest ancestor with `overflow-y: auto|scroll`, tracks its scroll position,
 * and paints a thin thumb that appears while scrolling and fades out after
 * `hideAfterMs`. It is `position: sticky` so it never takes layout width and
 * stays pinned to the visible area while the content moves underneath.
 */
export const ScrollFadeIndicator = ({
  color,
  hideAfterMs = 900,
}: ScrollFadeIndicatorProps) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<number | null>(null);
  const [thumb, setThumb] = useState<{ top: number; height: number } | null>(
    null
  );
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    let scroller: HTMLElement | null = anchor.parentElement;
    while (scroller) {
      const overflowY = getComputedStyle(scroller).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") break;
      scroller = scroller.parentElement;
    }
    if (!scroller) return;
    const target = scroller;

    const measure = () => {
      const { scrollTop, scrollHeight, clientHeight } = target;
      if (scrollHeight <= clientHeight + 1) {
        setThumb(null);
        return;
      }
      const ratio = clientHeight / scrollHeight;
      const height = Math.max(MIN_THUMB_PX, Math.round(clientHeight * ratio));
      const maxTop = clientHeight - height;
      const top = Math.round(
        (scrollTop / (scrollHeight - clientHeight)) * maxTop
      );
      setThumb({ top, height });
    };

    const onScroll = () => {
      measure();
      setVisible(true);
      if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
      hideTimer.current = window.setTimeout(() => {
        setVisible(false);
        hideTimer.current = null;
      }, hideAfterMs);
    };

    measure();
    target.addEventListener("scroll", onScroll, { passive: true });
    const resize = new ResizeObserver(measure);
    resize.observe(target);
    for (const child of Array.from(target.children)) resize.observe(child);

    return () => {
      target.removeEventListener("scroll", onScroll);
      resize.disconnect();
      if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
    };
  }, [hideAfterMs]);

  return (
    <div ref={anchorRef} className="scroll-fade-indicator" aria-hidden="true">
      {thumb ? (
        <span
          className={`scroll-fade-indicator__thumb${
            visible ? " is-visible" : ""
          }`}
          style={
            {
              "--thumb-top": `${thumb.top}px`,
              "--thumb-height": `${thumb.height}px`,
              "--thumb-color": color,
            } as CSSProperties
          }
        />
      ) : null}
    </div>
  );
};
